/**
 * Blanks the outline columns a learning mode does not produce — website only.
 *
 * The LMS does this server-side (`CourseMatrixHelper`) and reloads the page. The public page has no
 * save and no reload, so it repaints from the payload that same helper serialised into
 * `data-mode-columns`. Nothing is stored: every load starts on the server-rendered mode.
 *
 * Only ever blanks, never reveals — the server renders Verified Learning, whose column set every
 * other mode is a subset of.
 *
 * ⛔ Queries `.matrix-cell.is-pre` / `.is-lesson` / `.is-post` and `.matrix-head__cell.is-*`, which
 * are styling classes. Deliberate: those three ARE the column contract — `CourseMatrixHelper` blanks
 * by them and `common/course-matrix` styles by them — so a `js-` alias would be a third encoding of
 * one fact. `nth-child` is not an option: the band, module and non-demo rows use `colspan`.
 */
export default class CourseOutlineModeFilter {
    constructor() {
        this.classList = {
            root: 'js-course-outline-modes',
            cell: 'matrix-cell',
            head: 'matrix-head__cell',
            blank: 'is-blank',
            muted: 'is-muted',
        };

        this.columns = ['pre', 'lesson', 'post'];
        this.root = document.querySelector(`.${this.classList.root}`);

        if (!this.root) {
            return;
        }

        this.modes = this.readModes();

        if (!this.modes) {
            return;
        }

        this.$onChange = this.onChange.bind(this);

        document.addEventListener('changeLearningMode', this.$onChange);
    }

    /**
     * @return {object|null}
     */
    readModes() {
        try {
            return JSON.parse(this.root.dataset.modeColumns);
        } catch (error) {
            return null;
        }
    }

    /**
     * @param {CustomEvent} event
     */
    onChange(event) {
        this.apply(event.detail.learningModeID);
    }

    /**
     * @param {string} mode
     */
    apply(mode) {
        const config = this.modes[mode];

        if (!config) {
            return;
        }

        this.columns.forEach((column) => {
            const isBlank = Boolean(config.hide[column]);

            this.root.querySelectorAll(`.${this.classList.cell}.is-${column}`)
                .forEach((cell) => cell.classList.toggle(this.classList.blank, isBlank));

            // Not one node: every module table repeats the header for mobile.
            this.root.querySelectorAll(`.${this.classList.head}.is-${column}`).forEach((head) => {
                head.textContent = config.labels[column];
                head.classList.toggle(this.classList.muted, Boolean(config.muted[column]));
            });
        });
    }

    destroy() {
        document.removeEventListener('changeLearningMode', this.$onChange);
    }
}
